import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import CopyButton from "@/components/CopyButton";
import MarkdownLite from "@/components/MarkdownLite";
import { Pencil, Eye, FileDown, Save, Loader2, Check } from "lucide-react";
import { ctaUrl, ctaTextLine } from "@/lib/studyText";

// Browser print-to-PDF (shared). Keeps the existing lightweight export flow.
export function printSermon(title, text) {
  const w = window.open("", "_blank");
  if (!w) return;
  const safeTitle = (title || "Sermon outline").replace(/&/g, "&amp;").replace(/</g, "&lt;");
  const body = (text || "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
  const link = ctaUrl("pdf", "sermon_export");
  w.document.write(`<!doctype html><html><head><title>${safeTitle}</title>
    <style>body{font-family:Georgia,serif;color:#161D2F;max-width:640px;margin:40px auto;line-height:1.6;padding:0 24px}
    h1{font-size:24px}h2{font-size:20px;color:#00E5FF;margin-top:24px}h3{font-size:17px;margin-top:16px}
    .tag{font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#00E5FF}
    .ow-cta{margin-top:32px;padding-top:14px;border-top:1px solid #1E273D;font-family:system-ui,sans-serif;font-size:11px;color:#3A4763;line-height:1.7}
    .ow-cta strong{color:#00E5FF} .ow-cta a{color:inherit}</style></head>
    <body><div class="tag">OpenWord AI \u00b7 Sermon outline</div>
    <pre style="white-space:pre-wrap;font-family:Georgia,serif">${body}</pre>
    <div class="ow-cta"><strong>Turn any passage into a sermon outline free with OpenWord AI</strong><br/><a href="${link}">${link}</a></div>
    </body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 300);
}

// Reusable: preview / edit toggle + copy + save (optional) + PDF for a sermon outline.
export default function SermonEditor({ sermon, onSermonChange, title, onSave, saving, testid = "sermon" }) {
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    if (onSave) {
      try {
        await onSave(sermon);
      } catch {
        return;  // persistence failed — stay in edit mode
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    }
    setEditing(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <button
          data-testid={`${testid}-toggle-edit`}
          onClick={() => setEditing((e) => !e)}
          className="flex items-center gap-1.5 text-sm text-[#00E5FF] hover:text-[#00B8CC] transition-colors"
        >
          {editing ? <><Eye className="w-4 h-4" strokeWidth={1.5} /> Preview</> : <><Pencil className="w-4 h-4" strokeWidth={1.5} /> Edit</>}
        </button>
        <CopyButton text={`${sermon}\n\n\u2014\n${ctaTextLine("Build sermon outlines free with OpenWord AI:", "copy", "sermon_export")}`} label="Sermon copied" testid={`${testid}-copy`} />
      </div>

      {editing ? (
        <Textarea
          data-testid={`${testid}-editor`}
          value={sermon}
          onChange={(e) => onSermonChange(e.target.value)}
          className="min-h-[22rem] max-h-[26rem] text-[13px] leading-relaxed font-mono border-[#29344D] focus-visible:ring-1 focus-visible:ring-[#00E5FF]/30"
        />
      ) : (
        <div
          data-testid={`${testid}-output`}
          className="rounded-xl bg-[#07090E] border border-[#1E273D] p-4 text-[15px] text-slate-300 max-h-[26rem] overflow-y-auto"
        >
          <MarkdownLite text={sermon} />
        </div>
      )}

      <div className="flex items-center gap-2">
        {editing && onSave && (
          <Button
            data-testid={`${testid}-save`}
            onClick={save}
            disabled={saving}
            className="rounded-full text-white"
            style={{ backgroundColor: "#00E5FF" }}
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving&hellip;</>
              : saved ? <><Check className="w-4 h-4 mr-2" strokeWidth={2} /> Saved</>
              : <><Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save edits</>}
          </Button>
        )}
        <Button
          data-testid={`${testid}-pdf`}
          onClick={() => printSermon(title, sermon)}
          variant="outline"
          className="rounded-full border-[#29344D] text-slate-400"
        >
          <FileDown className="w-4 h-4 mr-2" strokeWidth={1.5} /> PDF
        </Button>
      </div>
    </div>
  );
}
